// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import {
  client,
  serverUrl,
  apiEndpoints,
  filesystem_root,
  subdomain,
} from "../../config";
import { getExternal } from "../../core/getNode";
import { getOnlineNode } from "../../core/getNode";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import CommentSection from "../../partials/comments/commentSection";
import { logCustomEvent } from "../../core/analytics";
import Reactions from "../../partials/comments/reactions";
import { favoritesContentType } from "../../utils/helpers";
import PostBodyParser from "../../partials/posts/postBodyParser";

// UI components
import SinglesHeader from "../../partials/singlesHeader";
import { Row } from "react-bootstrap";

export default function PostSingle(props) {
  const {
    apiEndpoint,
    entityFavoritesContentType,
    preview,
    previewReturnRoute,
    returnRoute,
    backBtnText,
    screenClassName,
    v2Bundle,
    logCustomEventType,
    entityType,
    setEntityPost,
    featuredImageEnabled,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [editorData, setEditorData] = useState({});

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");
  const contentType = favoritesContentType(entityFavoritesContentType);

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  const loadNode = () => {
    const id = params.id;

    if (id) {
      if (props.preview) {
        getContent(id);
      } else {
        getContentFromStaticFS(id);
      }
      // getComments(id)
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  /**
   * @function setError
   * @description Updates the state of the component upon an error
   * @param {boolean} _isError
   * @param {int} _errorStatus
   * @param {string} _errorMessage
   */
  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  /**
   * @function getContent
   * @description Retrieves the data from an API / Fallback to local realm object if there is no connection
   */
  const getContent = (id) => {
    let path;
    if (client === "companyapp") {
      path = `${apiEndpoint}/${id}?_format=json`;

      if (hash) {
        path = `${path}&auHash=${hash}`;
      }
    } else {
      path = `${apiEndpoint}/all/${id}?_format=json&status=1&promote=1`;

      if (hash) {
        path = `/api/v2/shared?bundle=${v2Bundle}&nid=${id}&authkey=${hash}`;
      }
    }
    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.status === 200) {
          console.log("single post", response.data);
          setPost(response.data);
          setEntityPost(response.data);
          setEditorData(response.data.body);
          logCustomEvent("read", response.data, logCustomEventType);
          setIsError(false);
          setIsLoading(false);
        } else {
          setErrorStatus(404);
          setErrorMessage("No data");
          setIsError(true);
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setErrorStatus(403);
          setErrorMessage("You do not have access to view this content.");
          setIsError(true);
          setIsLoading(false);
        } else {
          setErrorStatus(500);
          setErrorMessage("Failed to get data test");
          setIsError(true);
          setIsLoading(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getContentFromStaticFS = (id) => {
    let path = `${filesystem_root}/${subdomain}/${entityType}/${id}/${id}/book.json`;
    getExternal(path, "get")
      .then((response) => {
        console.log("single post", response.data);
        setPost(response.data);
        setEntityPost(response.data);
        setEditorData(response.data.body);
        setIsError(false);
        setIsLoading(false);
        console.log(response);
      })
      .catch((error) => {
        setErrorStatus(error?.response?.status || 500);
        setErrorMessage("Failed to get data");
        setIsError(true);
        setIsLoading(false);
      });
  };

  const renderFeaturedImage = () => {
    if (!post.field_featured_image) {
      return null;
    }
    const fileToken = post.file_access_token;

    const imgSrc = props.preview
      ? `${serverUrl}/api/v2/${post.field_featured_image}`
      : `${filesystem_root}/${subdomain}/${entityType}/${post.id}/${post.id}/${post.field_featured_image}`;

    return (
      <div className="img-container ratio ratio-16x9 w-100 mb-4">
        <img src={imgSrc} alt="" className="single-screen-img" />
      </div>
    );
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  } else {
    if (isError) {
      return <Error status={errorStatus} message={errorMessage} />;
    } else {
      let category = "Uncategorised";

      if (post.category_labels) {
        category = post.category_labels;
      }

      return (
        <main className={`${screenClassName} single`} data-id={post.id}>
          <article className="article">
            <SinglesHeader
              route={preview ? previewReturnRoute : returnRoute}
              backBtnText={backBtnText}
              category={category}
              timestamp={post.created}
              title={post.title}
              hash={hash}
            />
            {featuredImageEnabled &&
              post.field_featured_image &&
              renderFeaturedImage()}
            {props.children}
            <PostBodyParser
              editorData={editorData}
              preview={props.preview}
              entityType={entityType}
              postId={post.id}
            />
            {user && user.current_user && (
              <>
                <Row className="reaction-container gx-0">
                  <Reactions
                    user={user}
                    nid={post.id}
                    contentType={contentType}
                    field_allow_reactions={post.field_allow_reactions}
                    field_access_token={post.field_access_token}
                    title={post.title}
                    shareEnabled
                  />
                </Row>
                <CommentSection
                  nid={post.id}
                  field_allow_reactions={post.field_allow_reactions}
                  field_allow_comments={post.field_allow_comments}
                />
              </>
            )}
          </article>
        </main>
      );
    }
  }
}
